import axios from 'api';

// Super-admin spec for vendors & vendor models, gated by `IsPublicSchemaSuperUser`.
// Three families of endpoints live in this file:
//
//   1. Vendor parent CRUD     /api/super/vendors/{kind}/         (§5)
//   2. Vendor model CRUD      /api/super/vendor-models/{kind}/   (§6)
//   3. Bulk lifecycle, set-default, credentials, registry, catalog preview
//
// kind ∈ "llm" | "asr" | "tts". The registry refresh also accepts "all".

// ── Bulk lifecycle — /api/super/vendor-models/{kind}/ ─────────────────────

// Body: { ids, replacement_slug?, deprecation_note?, retirement_date? }
export const deprecateVendorModels = (kind, body) => {
  return axios.post(`/api/super/vendor-models/${kind}/deprecate/`, body);
};

// Body: { ids }
export const retireVendorModels = (kind, body) => {
  return axios.post(`/api/super/vendor-models/${kind}/retire/`, body);
};

// Body: { ids, tier_status } — tier_status ∈ "current" | "legacy" | "preview" | ...
export const setVendorModelsTierStatus = (kind, body) => {
  return axios.post(`/api/super/vendor-models/${kind}/tier-status/`, body);
};

// ── Vendor credentials — /api/super/vendors/{kind}/{vendor_id}/credentials/ ──

// Body: { api_key } — rotated key is pushed to AWS Secrets Manager.
export const rotateVendorCredentials = (kind, vendor_id, body) => {
  return axios.post(`/api/super/vendors/${kind}/${vendor_id}/credentials/`, body);
};

// Body (optional): { api_key } — omit to validate the stored key.
export const validateVendorCredentials = (kind, vendor_id, body = {}) => {
  return axios.post(`/api/super/vendors/${kind}/${vendor_id}/credentials/validate/`, body);
};

// ── Defaults ─────────────────────────────────────────────────────────────

// Vendor-level, kind-wide singleton — promotes this vendor to the default
// for its kind. Any other vendor of the same kind is atomically demoted.
export const setDefaultVendor = (kind, vendor_id) => {
  return axios.post(`/api/super/vendors/${kind}/${vendor_id}/set-default/`);
};

// Model-level, per-vendor singleton — promotes this model to the default
// within its parent vendor. Any sibling model of the same vendor is
// atomically demoted.
export const setDefaultVendorModel = (kind, model_id) => {
  return axios.post(`/api/super/vendor-models/${kind}/${model_id}/set-default/`);
};

// ── Registry refresh — /api/super/registry/refresh/ ──────────────────────

// Body: { kind } — kind ∈ "llm" | "asr" | "tts" | "all".
export const refreshVendorRegistry = (kind) => {
  return axios.post(`/api/super/registry/refresh/`, { kind });
};

// ── Vendor models CRUD — /api/super/vendor-models/{kind}/ ──────────────
//
// The kind-specific helpers below thunk through to a single shared
// implementation. The backend serializer uses ``fields = "__all__"``
// so the payload shape mirrors the model — see API_REFERENCE.md
// Section 6 for the full per-kind field list.
//
// Pass the parent vendor either as ``vendor: <pk>`` (preferred) or as
// ``vendor__slug: <slug>`` — the backend accepts either filter param.

const listVendorModels = (kind, params, signal) =>
  axios.get(`/api/super/vendor-models/${kind}/`, { params, signal });

const createVendorModel = (kind, payload) =>
  axios.post(`/api/super/vendor-models/${kind}/`, payload);

const patchVendorModel = (kind, id, payload) =>
  axios.patch(`/api/super/vendor-models/${kind}/${id}/`, payload);

const removeVendorModel = (kind, id) =>
  axios.delete(`/api/super/vendor-models/${kind}/${id}/`);

// LLM model CRUD — the redux store passes ``params`` for list and a
// flat object containing ``id`` for update, matching the legacy shape.
export const getVendorModelsData = (params, signal) =>
  listVendorModels('llm', params, signal);
export const createVendorModels = (payload) =>
  createVendorModel('llm', payload);
export const updateVendorModels = ({ id, ...payload }) =>
  patchVendorModel('llm', id, payload);
export const deleteVendorModels = (id) =>
  removeVendorModel('llm', id);

// ASR model CRUD.
export const getASRVendorModelsData = (params, signal) =>
  listVendorModels('asr', params, signal);
export const createASRVendorModels = (payload) =>
  createVendorModel('asr', payload);
export const updateASRVendorModels = ({ id, ...payload }) =>
  patchVendorModel('asr', id, payload);
export const deleteASRVendorModels = (id) =>
  removeVendorModel('asr', id);

// TTS model CRUD.
export const getTTSVendorModelsData = (params, signal) =>
  listVendorModels('tts', params, signal);
export const createTTSVendorModels = (payload) =>
  createVendorModel('tts', payload);
export const updateTTSVendorModels = ({ id, ...payload }) =>
  patchVendorModel('tts', id, payload);
export const deleteTTSVendorModels = (id) =>
  removeVendorModel('tts', id);

// ── Vendor parent CRUD — /api/super/vendors/{kind}/ ────────────────────
//
// The serializer excludes the live `data` JSON column (API key payload) on
// every read/write — rotate via the credential endpoints above. A computed
// `has_credentials` boolean (true iff `data` is non-empty) is surfaced so
// the UI can show a "Set / Rotate" affordance without seeing the secret.
// LLM rows include `base_url`; TTS/ASR rows include `controls`; TTS adds
// `requires_model_selection`.

const _listVendors = (kind, params, signal) =>
  axios.get(`/api/super/vendors/${kind}/`, { params, signal });

const _getVendor = (kind, id, signal) =>
  axios.get(`/api/super/vendors/${kind}/${id}/`, { signal });

const _createVendor = (kind, payload) =>
  axios.post(`/api/super/vendors/${kind}/`, payload);

const _patchVendor = (kind, id, payload) =>
  axios.patch(`/api/super/vendors/${kind}/${id}/`, payload);

const _deleteVendor = (kind, id) =>
  axios.delete(`/api/super/vendors/${kind}/${id}/`);

// LLM vendor CRUD — same signature shape as the model CRUD above so
// callers can swap the resource without changing the call site.
export const getVendorData = (params, signal) => _listVendors('llm', params, signal);
export const getVendorById = (id, signal) => _getVendor('llm', id, signal);
export const createVendor = (payload) => _createVendor('llm', payload);
export const updateVendor = ({ id, ...payload }) => _patchVendor('llm', id, payload);
export const deleteVendor = (id) => _deleteVendor('llm', id);

// ASR vendor CRUD.
export const getASRVendorData = (params, signal) => _listVendors('asr', params, signal);
export const getASRVendorById = (id, signal) => _getVendor('asr', id, signal);
export const createASRVendor = (payload) => _createVendor('asr', payload);
export const updateASRVendor = ({ id, ...payload }) => _patchVendor('asr', id, payload);
export const deleteASRVendor = (id) => _deleteVendor('asr', id);

// TTS vendor CRUD.
export const getTTSVendorData = (params, signal) => _listVendors('tts', params, signal);
export const getTTSVendorById = (id, signal) => _getVendor('tts', id, signal);
export const createTTSVendor = (payload) => _createVendor('tts', payload);
export const updateTTSVendor = ({ id, ...payload }) => _patchVendor('tts', id, payload);
export const deleteTTSVendor = (id) => _deleteVendor('tts', id);

// ── Bulk import — /api/super/{vendors|vendor-models}/{kind}/bulk-import/ ─
//
// Body is a raw JSON array (vendor.orphix.ai catalog shape) — paste or
// upload the registry JSON straight in. Server upserts by natural key
// (vendors: name; models: vendor+slug) and fans out to all tenants.
// Response: { created, updated, skipped, errors, tenant_fanout }.
//
// kind ∈ "llm" | "asr" | "tts".

// `retireMissing` (default true) tells the server to flip is_active=false
// + status="retired" on any DB row that isn't in the uploaded array.
// Pass false for genuinely partial uploads where you only mean to merge.
const _retireParams = (retireMissing) => ({
  params: { retire_missing: retireMissing ? 'true' : 'false' }
});

export const bulkImportVendors = (kind, rows, retireMissing = true) =>
  axios.post(`/api/super/vendors/${kind}/bulk-import/`, rows, _retireParams(retireMissing));

export const bulkImportVendorModels = (kind, rows, retireMissing = true) =>
  axios.post(`/api/super/vendor-models/${kind}/bulk-import/`, rows, _retireParams(retireMissing));

// Voices live under /api/super/reference/voices/ — they're TTS-only and
// don't need a kind selector. Keyed on voice_id; fans out via TenantVoice.
//
// Unlike vendors/models, voices are *always* enqueued as a Celery task
// (3k-row catalog × tenant fan-out doesn't fit inside a gunicorn worker
// timeout). The POST returns immediately with { task_id, total,
// status_url } and the panel polls bulkImportTaskStatus(task_id) until
// it reports SUCCESS or FAILURE.
export const bulkImportVoices = (rows, retireMissing = true) =>
  axios.post(`/api/super/reference/voices/bulk-import/`, rows, _retireParams(retireMissing));

export const bulkImportTaskStatus = (taskId) =>
  axios.get(`/api/super/bulk-import/${taskId}/`);

// ── Catalog preview — /api/super/catalog/{kind}/{resource}/ ────────────
//
// Read-only side-by-side: upstream Vendor Pricing API (vendor.orphix.ai)
// + local DB + a set-based diff keyed by the resource's unique key
// (vendors → slug, models → slug, voices → voice_id). resource ∈
// "vendors" | "models" | "voices". "voices" is TTS-only — calling it
// with kind ≠ "tts" returns 400.
//
// Network-resilient: if upstream is unreachable the endpoint still
// returns 200 with `upstream.available=false` and the local half so the
// UI degrades gracefully.

export const getCatalog = (kind, resource, signal) =>
  axios.get(`/api/super/catalog/${kind}/${resource}/`, { signal });
